import { uploadTelemetryService } from "@/utils/uploadTelemetry";
import { UPLOAD_EVENT } from "../diagnostics/UploadEvents";

/**
 * Legacy upload path uses a single multipart request with no per-file
 * granularity, so the bridge only needs to translate uploadTelemetryService
 * status transitions.
 *
 * Byte-progress events fire from the axios onUploadProgress callback into
 * uploadTelemetryService.recordProgress() — we surface those to diagnostics
 * as CHUNK_COMPLETED (with a synthetic sourceFileId of "legacy-body") so the
 * timeline shape stays consistent with the chunked path. Downstream code that
 * segments by chunk vs package should filter on the sourceFileId.
 *
 * @param {import('../diagnostics/UploadDiagnostics').UploadDiagnostics} diagnostics
 * @returns {() => void} disposer
 */
export const attachLegacyUploadBridge = (diagnostics) => {
  const uploadId = diagnostics.uploadId;
  let lastStatus = null;
  let lastLoaded = 0;

  const unsub = uploadTelemetryService.subscribe(uploadId, (state) => {
    if (!state) return;

    diagnostics.patchState({
      bytesUploaded: state.uploadedBytes ?? diagnostics.getState().bytesUploaded,
      totalBytes: state.totalBytes ?? diagnostics.getState().totalBytes,
    });

    // Emit CHUNK_COMPLETED whenever the byte counter advances (deduped at 1KB).
    if (
      typeof state.uploadedBytes === "number" &&
      state.uploadedBytes > lastLoaded + 1024
    ) {
      diagnostics.record(UPLOAD_EVENT.CHUNK_COMPLETED, {
        sourceFileId: "legacy-body",
        bytesUploaded: state.uploadedBytes,
        totalBytes: state.totalBytes,
        legacy: true,
      });
      lastLoaded = state.uploadedBytes;
    }

    if (state.status === lastStatus) return;
    const previous = lastStatus;
    lastStatus = state.status;

    switch (state.status) {
      case uploadTelemetryService.STATUS.UPLOADING:
        if (previous == null) {
          diagnostics.record(UPLOAD_EVENT.UPLOAD_STARTED, {
            totalBytes: state.totalBytes,
            legacy: true,
          });
        }
        break;
      case uploadTelemetryService.STATUS.FINALIZING:
        diagnostics.info("upload_finalizing", { legacy: true });
        break;
      case uploadTelemetryService.STATUS.COMPLETED:
        diagnostics.record(UPLOAD_EVENT.UPLOAD_COMPLETED, {
          bytesUploaded: state.uploadedBytes,
          durationMs: (state.elapsedTime || 0) * 1000,
          averageBps: state.averageBps,
          legacy: true,
        });
        break;
      case uploadTelemetryService.STATUS.FAILED:
        diagnostics.record(UPLOAD_EVENT.UPLOAD_FAILED, {
          previousStatus: previous,
          bytesUploaded: state.uploadedBytes,
          legacy: true,
        });
        break;
      default:
        break;
    }
  });

  return () => {
    try {
      unsub();
    } catch (_error) {
      /* ignore */
    }
  };
};
